import localizationService from './localizationService'
import AuditService from './auditService'
import * as Sentry from "@sentry/react"

// Types réutilisés depuis subscriptionService
export interface SubscriptionResponse {
  status: 'active' | 'expired' | 'suspended' | 'cancelled' | 'trial'
  expiryDate?: string
  plan: string
  price: number
  nextBilling?: string
  daysUntilNext?: number
  statusMessage?: string
  trialEndDate?: string
  hoursRemaining?: number
}

export interface PaymentResult {
  success: boolean
  data?: {
    orderId: string
    formToken: string
    amount: number
  }
  message?: string
}

export interface TrialSubscriptionResponse {
  success: boolean
  data?: {
    orderId: string
    formToken: string
    publicKey: string
    mode: string
    trialEndDate: string
    subscriptionId: string
    amount: number
  }
  message?: string
}

export interface TrialStatusResponse {
  success: boolean
  data?: {
    status: 'trial' | 'active' | 'expired' | 'cancelled'
    trialEndDate?: string
    hoursRemaining?: number
    subscriptionId?: string
  }
  message?: string
}

export class SubscriptionServiceZ {
  private baseUrl: string = 'https://payment.nexiamobile.com/api'
  private apiKey: string = 'sk_test_44117fbfdce2e926542c9fa98bebb5151792f13e2055924c'

  // Taux de conversion vers XPF pour chaque devise supportée
  private static readonly TO_XPF: Record<string, number> = {
    EUR: 119.332,
    USD: 109.50,
    GBP: 138.00,
    CAD: 80.50,
    AUD: 72.00,
    NZD: 66.50,
    XPF: 1
  }

  // Convertit un montant en centimes d'une devise vers XPF (sans centimes)
  private toXpf(amountCents: number, currency: string): number {
    const rate = SubscriptionServiceZ.TO_XPF[currency] || SubscriptionServiceZ.TO_XPF.USD
    return Math.round((amountCents / 100) * rate)
  }

  private getPlanConfigByCode(planCode: string, currency: string = 'EUR') {
    if (planCode === 'daily') {
      return {
        planName: 'Parking Unlimited Daily',
        frequency: 'daily',
        amount: this.toXpf(1499, currency)
      }
    }
    if (planCode === 'payg') {
      return {
        planName: 'Parking Pay As You Go',
        frequency: 'one-time',
        amount: this.toXpf(700, currency)
      }
    }

    return {
      planName: 'Parking Unlimited Monthly',
      frequency: 'monthly',
      amount: this.toXpf(4999, currency)
    }
  }

  async checkSubscriptionStatus(email: string): Promise<SubscriptionResponse | null> {
    try {
      console.log('🔍 [Z] Vérification du statut d\'abonnement pour:', email)

      const response = await fetch(`${this.baseUrl}/get-subscription-status?customerEmail=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      const result = await response.json()
      console.log('📋 [Z] Réponse API statut:', result)

      if (result.success && result.data && result.data.length > 0) {
        const activeSubscription = result.data.find((sub: any) => sub.status === 'active')

        if (activeSubscription) {
          return {
            status: 'active',
            expiryDate: activeSubscription.nextPaymentDate,
            plan: 'monthly',
            price: 49.99,
            nextBilling: activeSubscription.nextPaymentDate,
            daysUntilNext: activeSubscription.daysUntilNextPayment,
            statusMessage: activeSubscription.statusMessage
          }
        }

        const trialSubscription = result.data.find((sub: any) => sub.status === 'trial')

        if (trialSubscription) {
          return {
            status: 'trial',
            expiryDate: trialSubscription.nextPaymentDate,
            plan: 'monthly',
            price: 49.99,
            nextBilling: trialSubscription.nextPaymentDate,
            daysUntilNext: trialSubscription.daysUntilNextPayment,
            statusMessage: `Trial period - ${trialSubscription.daysUntilNextPayment} day(s) remaining`,
            trialEndDate: trialSubscription.nextPaymentDate
          }
        }

        const otherSubscription = result.data[0]
        if (otherSubscription) {
          return {
            status: otherSubscription.status === 'cancelled' ? 'cancelled' : 'expired',
            expiryDate: otherSubscription.nextPaymentDate,
            plan: 'monthly',
            price: 49.99,
            statusMessage: otherSubscription.statusMessage
          }
        }
      }

      return {
        status: 'expired',
        plan: 'monthly',
        price: 49.99
      }

    } catch (error) {
      console.error('❌ [Z] Erreur lors de la vérification du statut:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionServiceZ',
          method: 'checkSubscriptionStatus'
        },
        extra: {
          email,
          baseUrl: this.baseUrl
        }
      })
      return null
    }
  }

  async createSubscriptionWithPayment(
    email: string,
    userId?: string,
    options?: {
      planCode?: string
      planName?: string
      subscriptionAmount?: number
      initialAmount?: number
      frequency?: string
    }
  ): Promise<PaymentResult> {
    try {
      console.log('🚀 [Z] Création d\'abonnement avec paiement pour:', email)

      if (userId) {
        await AuditService.logUserActivity({
          userId,
          eventType: 'PAYMENT_INITIATED',
          details: { email, method: 'subscription_with_payment_z' }
        })
      }

      const paymentData = await localizationService.getPaymentData()
      console.log('💰 [Z] Données de paiement localisées:', paymentData)

      const planCode = options?.planCode || 'monthly'
      const sourceCurrency = paymentData.currency || 'EUR'
      const defaultPlan = this.getPlanConfigByCode(planCode, sourceCurrency)
      const planName = options?.planName || defaultPlan.planName
      const frequency = options?.frequency || defaultPlan.frequency
      // Convertir les montants de la devise localisée -> XPF
      const subscriptionAmount = options?.subscriptionAmount
        ? this.toXpf(options.subscriptionAmount, sourceCurrency)
        : defaultPlan.amount
      const initialAmount = options?.initialAmount
        ? this.toXpf(options.initialAmount, sourceCurrency)
        : subscriptionAmount

      let response;
      let result;

      if (planCode === 'payg') {
        const paygData = {
          amount: initialAmount,
          currency: 'XPF',
          orderId: `carstore_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
          customerEmail: email,
          customer: {
            email: email
          }
        };

        response = await fetch(`${this.baseUrl}/create-payment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.apiKey}`
          },
          body: JSON.stringify(paygData)
        });

        result = await response.json();
        console.log('📤 [Z] Réponse création paiement Pay As You Go:', result);
      } else {
        const subscriptionData = {
          customerEmail: email,
          planName,
          subscriptionAmount,
          initialAmount,
          frequency,
          currency: 'XPF',
          customerReference: `parking_${email}_${Date.now()}`
        };

        response = await fetch(`${this.baseUrl}/create-subscription-with-payment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.apiKey}`
          },
          body: JSON.stringify(subscriptionData)
        });

        result = await response.json();
        console.log('📤 [Z] Réponse création abonnement:', result);
      }

      if (userId && result.success && result.data) {
        await AuditService.logPaymentAudit({
          userId,
          paymentId: result.data.orderId,
          amount: paymentData.amount / 100,
          currency: 'XPF',
          paymentStatus: 'initiated'
        })
      }

      return result

    } catch (error) {
      console.error('❌ [Z] Erreur lors de la création de l\'abonnement:', error)

      if (userId) {
        await AuditService.logUserActivity({
          userId,
          eventType: 'PAYMENT_ERROR',
          details: {
            email,
            error: error instanceof Error ? error.message : 'Unknown error',
            method: 'subscription_with_payment_z'
          }
        })
      }

      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionServiceZ',
          method: 'createSubscriptionWithPayment'
        },
        extra: {
          email,
          userId,
          baseUrl: this.baseUrl
        }
      })

      return {
        success: false,
        message: error instanceof Error ? error.message : 'Erreur inconnue'
      }
    }
  }

  async getSubscriptionId(customerEmail: string): Promise<{ success: boolean; subscriptionId?: string; message?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/get-subscription-status?customerEmail=${encodeURIComponent(customerEmail)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      const result = await response.json()

      if (result.success && result.data && result.data.length > 0) {
        const activeSubscription = result.data.find((sub: any) => sub.status === 'active')
        if (activeSubscription && activeSubscription.id) {
          return { success: true, subscriptionId: activeSubscription.id }
        }

        const trialSubscription = result.data.find((sub: any) => sub.status === 'trial')
        if (trialSubscription && trialSubscription.id) {
          return { success: true, subscriptionId: trialSubscription.id }
        }

        const firstSubscription = result.data[0]
        if (firstSubscription && firstSubscription.id) {
          return { success: true, subscriptionId: firstSubscription.id }
        }
      }

      return { success: false, message: 'Aucun abonnement trouvé pour cet email' }

    } catch (error) {
      console.error('❌ [Z] Erreur lors de la récupération de l\'ID:', error)
      Sentry.captureException(error, {
        tags: { service: 'subscriptionServiceZ', method: 'getSubscriptionId' },
        extra: { customerEmail, baseUrl: this.baseUrl }
      })
      return { success: false, message: error instanceof Error ? error.message : 'Erreur inconnue' }
    }
  }

  async cancelSubscription(subscriptionIdOrEmail: string, isEmail: boolean = false, reason: string = 'Annulation demandée par l\'utilisateur'): Promise<{ success: boolean; message?: string }> {
    try {
      let subscriptionId = subscriptionIdOrEmail

      if (isEmail) {
        const idResult = await this.getSubscriptionId(subscriptionIdOrEmail)
        if (!idResult.success || !idResult.subscriptionId) {
          return { success: false, message: idResult.message || 'Impossible de récupérer l\'ID de l\'abonnement' }
        }
        subscriptionId = idResult.subscriptionId
      }

      const response = await fetch(`${this.baseUrl}/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          subscriptionId: subscriptionId,
          reason: reason,
          calculateRefund: false,
          immediateTermination: false
        })
      })

      const result = await response.json()

      if (result.success) {
        return { success: true, message: 'Abonnement annulé avec succès' }
      } else {
        return { success: false, message: result.message || 'Erreur lors de l\'annulation' }
      }

    } catch (error) {
      console.error('❌ [Z] Erreur lors de l\'annulation:', error)
      Sentry.captureException(error, {
        tags: { service: 'subscriptionServiceZ', method: 'cancelSubscription' },
        extra: { subscriptionIdOrEmail, isEmail, baseUrl: this.baseUrl }
      })
      return { success: false, message: error instanceof Error ? error.message : 'Erreur inconnue' }
    }
  }

  async getTransactionStatus(orderId: string): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/get-transaction-status?orderId=${orderId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      const result = await response.json()
      return result

    } catch (error) {
      console.error('❌ [Z] Erreur lors de la vérification du statut de transaction:', error)
      Sentry.captureException(error, {
        tags: { service: 'subscriptionServiceZ', method: 'getTransactionStatus' },
        extra: { orderId, baseUrl: this.baseUrl }
      })
      return { success: false, message: error instanceof Error ? error.message : 'Erreur inconnue' }
    }
  }

  async createPaidTrialFromPayg(
    email: string,
    trialAmount: number,
    userId?: string,
    fullName?: string
  ): Promise<TrialSubscriptionResponse> {
    try {
      console.log('🚀 [Z] Creating paid trial from PAYG for:', email)

      const paymentData = await localizationService.getPaymentData()

      const requestData = {
        customerEmail: email,
        planName: 'Premium Parking',
        subscriptionAmount: this.toXpf(paymentData.amount, paymentData.currency || 'EUR'),
        frequency: 'monthly',
        trialDurationHours: 24,
        trialAmount: this.toXpf(trialAmount, paymentData.currency || 'EUR'),
        currency: 'XPF',
        customer: {
          name: fullName || 'Premium User',
          phone: '+1 000 000 0000'
        }
      }

      const response = await fetch(`${this.baseUrl}/create-subscription-with-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify(requestData)
      })

      const result = await response.json()
      console.log('📤 [Z] Paid trial from PAYG response:', result)

      return result

    } catch (error) {
      console.error('❌ [Z] Error creating paid trial from PAYG:', error)
      Sentry.captureException(error, {
        tags: { service: 'subscriptionServiceZ', method: 'createPaidTrialFromPayg' },
        extra: { email, trialAmount, userId, fullName, baseUrl: this.baseUrl }
      })
      return { success: false, message: error instanceof Error ? error.message : 'Unknown error' }
    }
  }
}

export const subscriptionServiceZ = new SubscriptionServiceZ()
